import { useOpstelClient } from "@opstel/contexts/opstelClient/useOpstelClient";
import { instances } from "@opstel/query/queryKeys/instanceKeys";
import { UpdateAction } from "@opstel/query/v2/opstelClient";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions";

interface MutationData {
  startTime: DateTime<true>;
  routeId: string;
}

function mutationDataToUpdateAction(data: Partial<MutationData>): UpdateAction {
  return {
    ...(data.startTime ? { startTime: data.startTime.toUTC().toISO() } : {}),
    ...(data.routeId ? { routeId: data.routeId } : {}),
  };
}

export function useActionMutation({ actionId }: { actionId: string }) {
  const { instanceId } = useCurrentInstanceId();
  const { opstelClient } = useOpstelClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<MutationData>) =>
      opstelClient.updateAction(
        { instanceId: instanceId ?? "", actionId },
        mutationDataToUpdateAction(data),
      ),
    onSuccess: async () => {
      messageToast({
        type: "default",
        message: "Actie is opgeslagen",
      });

      const invalidateSbdData = queryClient.invalidateQueries({
        queryKey: instances.current(instanceId).sbdData,
      });
      const invalidateActions = queryClient.invalidateQueries({
        queryKey: instances.current(instanceId).actions,
      });
      const invalidateActionMutations = queryClient.invalidateQueries({
        queryKey: instances.current(instanceId).action(actionId).allowedMutations,
      });
      const invalidateConflicts = queryClient.invalidateQueries({
        queryKey: instances.current(instanceId).conflicts,
      });

      await Promise.all([
        invalidateSbdData,
        invalidateActions,
        invalidateActionMutations,
        invalidateConflicts,
      ]);
    },
    onError: (error) => {
      messageToast({
        type: "error",
        message: error.message,
      });
    },
  });
}
